import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { getGraphQLClient } from '@/common/graphqlClient';
import { QueryKeys } from '@/common/queryKeys';
import { graphql } from '@/generated/graphql';
import type {
  DeleteDriverDistrictLegacyMutationVariables,
  GetDistrictsWithDriversLegacyQueryVariables,
  GetDriversWithDistrictsLegacyQueryVariables,
  InsertDriverDistrictLegacyMutationVariables,
  UpdateDistrictDeliveryDaysLegacyMutationVariables,
  UpdateDistrictLoadFactorLegacyMutationVariables,
  UpdateDistrictOffDaysLegacyMutationVariables,
} from '@/generated/graphql-types';

export const GET_DRIVERS_WITH_DISTRICTS_LEGACY = graphql(`
  query GetDriversWithDistrictsLegacy($departmentId: Int!, $searchText: String) {
    user(
      where: {
        roles: { _contains: "driver" }
        blocked: { _eq: false }
        departments_users: { departmentId: { _eq: $departmentId } }
        name: { _ilike: $searchText }
      }
    ) {
      id
      name
      driver_districts {
        id
        district {
          id
          enabled
          name
        }
      }
    }
    district(
      where: {
        departments_districts: { departmentId: { _eq: $departmentId } }
        name: { _ilike: $searchText }
        district_delivery_providers: {}
      }
      order_by: { name: asc }
    ) {
      id
      name
      enabled
    }
  }
`);

export const GET_DISTRICTS_WITH_DRIVERS_LEGACY = graphql(`
  query GetDistrictsWithDriversLegacy($departmentId: Int!, $searchText: String) {
    district(
      where: {
        departments_districts: { departmentId: { _eq: $departmentId } }
        name: { _ilike: $searchText }
      }
      order_by: { name: asc }
    ) {
      id
      name
      enabled
      loadFactor
      deliveryDays
      offDays
      driver_districts { driverId driver { name } }
    }
  }
`);

export const INSERT_DRIVER_DISTRICT_LEGACY = graphql(`
  mutation InsertDriverDistrictLegacy($driverId: String!, $districtId: String!) {
    insert_driver_district_one(object: { districtId: $districtId, driverId: $driverId }) { id }
  }
`);

export const DELETE_DRIVER_DISTRICT_LEGACY = graphql(`
  mutation DeleteDriverDistrictLegacy($districtId: String!, $driverId: String!) {
    delete_driver_district(where: { driverId: { _eq: $driverId }, districtId: { _eq: $districtId } }) {
      affected_rows
    }
  }
`);

export const UPDATE_DISTRICT_LOADFACTOR_LEGACY = graphql(`
  mutation UpdateDistrictLoadFactorLegacy($loadFactor: Int, $districtId: String) {
    update_district(where: { id: { _eq: $districtId } }, _set: { loadFactor: $loadFactor }) { affected_rows }
  }
`);

export const UPDATE_DISTRICT_DELIVERYDAYS_LEGACY = graphql(`
  mutation UpdateDistrictDeliveryDaysLegacy($deliveryDays: Int, $districtId: String) {
    update_district(where: { id: { _eq: $districtId } }, _set: { deliveryDays: $deliveryDays }) { affected_rows }
  }
`);

export const UPDATE_DISTRICT_OFFDAYS_LEGACY = graphql(`
  mutation UpdateDistrictOffDaysLegacy($offDays: jsonb, $districtId: String) {
    update_district(where: { id: { _eq: $districtId } }, _set: { offDays: $offDays }) { affected_rows }
  }
`);

export const useGetDriversWithDistrictsLegacy = (
  variables: GetDriversWithDistrictsLegacyQueryVariables
) => {
  const client = getGraphQLClient();
  return useQuery({
    queryKey: [
      QueryKeys.GET_DRIVERS_WITH_DISTRICTS_LEGACY,
      variables.departmentId,
      variables.searchText,
    ],
    queryFn: () => client.request(GET_DRIVERS_WITH_DISTRICTS_LEGACY, variables),
    enabled: Boolean(variables.departmentId),
  });
};

export const useGetDistrictsWithDriversLegacy = (
  variables: GetDistrictsWithDriversLegacyQueryVariables
) => {
  const client = getGraphQLClient();
  return useQuery({
    queryKey: [
      QueryKeys.GET_DISTRICTS_WITH_DRIVERS_LEGACY,
      variables.departmentId,
      variables.searchText,
    ],
    queryFn: () => client.request(GET_DISTRICTS_WITH_DRIVERS_LEGACY, variables),
    enabled: Boolean(variables.departmentId),
  });
};

export const useInsertDriverDistrictLegacy = () => {
  const client = getGraphQLClient();
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (variables: InsertDriverDistrictLegacyMutationVariables) =>
      client.request(INSERT_DRIVER_DISTRICT_LEGACY, variables),
    onSuccess: async (_data, _variables) => {
      await queryClient.invalidateQueries({
        queryKey: [QueryKeys.GET_DRIVERS_WITH_DISTRICTS_LEGACY],
      });
      await queryClient.invalidateQueries({
        queryKey: [QueryKeys.GET_DISTRICTS_WITH_DRIVERS_LEGACY],
      });
    },
  });
};

export const useDeleteDriverDistrictLegacy = () => {
  const client = getGraphQLClient();
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (variables: DeleteDriverDistrictLegacyMutationVariables) =>
      client.request(DELETE_DRIVER_DISTRICT_LEGACY, variables),
    onSuccess: async () => {
      await queryClient.invalidateQueries({
        queryKey: [QueryKeys.GET_DRIVERS_WITH_DISTRICTS_LEGACY],
      });
      await queryClient.invalidateQueries({
        queryKey: [QueryKeys.GET_DISTRICTS_WITH_DRIVERS_LEGACY],
      });
    },
  });
};

export const useUpdateDistrictLoadFactorLegacy = () => {
  const client = getGraphQLClient();
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (variables: UpdateDistrictLoadFactorLegacyMutationVariables) =>
      client.request(UPDATE_DISTRICT_LOADFACTOR_LEGACY, variables),
    onSuccess: async () => {
      await queryClient.invalidateQueries({
        queryKey: [QueryKeys.GET_DISTRICTS_WITH_DRIVERS_LEGACY],
      });
    },
  });
};

export const useUpdateDistrictDeliveryDaysLegacy = () => {
  const client = getGraphQLClient();
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (variables: UpdateDistrictDeliveryDaysLegacyMutationVariables) =>
      client.request(UPDATE_DISTRICT_DELIVERYDAYS_LEGACY, variables),
    onSuccess: async () => {
      await queryClient.invalidateQueries({
        queryKey: [QueryKeys.GET_DISTRICTS_WITH_DRIVERS_LEGACY],
      });
    },
  });
};

export const useUpdateDistrictOffDaysLegacy = () => {
  const client = getGraphQLClient();
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (variables: UpdateDistrictOffDaysLegacyMutationVariables) =>
      client.request(UPDATE_DISTRICT_OFFDAYS_LEGACY, variables),
    onSuccess: async () => {
      await queryClient.invalidateQueries({
        queryKey: [QueryKeys.GET_DISTRICTS_WITH_DRIVERS_LEGACY],
      });
    },
  });
};
